import { useState } from 'react';
import { S } from '../../domain/state.js';
import { cats } from '../../domain/categories.js';
import { getTxSrc } from '../../domain/tx.js';
import { keyOfISO, periodOptions, periodLabel, periodShort, curKey } from '../../domain/periods.js';
import { findRecFor, recEvery } from '../../domain/recurring.js';
import { noTags } from '../../lib/text.js';
import { fmt } from '../../lib/format.js';
import { Sheet, Seg, confirmDialog } from '../../components/ui.jsx';
import { Icon } from '../../components/Icon.jsx';
import { openModal, closeModal } from '../../app/nav.js';
import { updateTx, recWrite, deleteRec, deleteTx } from '../../store/actions.js';
import { toast } from '../../store/ui.js';
import { FREQS } from '../settings/RecModal.jsx';

export function openEditTx(id) {
  openModal(() => <EditTx id={id} />);
}

function EditTx({ id }) {
  const t = S.tx.find((x) => x.id === id);
  const [f, setF] = useState(() => {
    const ex = t ? findRecFor(t) : null;
    return {
      description: t ? t.description || '' : '',
      amount: t ? t.amount : '',
      date: t ? String(t.date || '').slice(0, 10) : '',
      month: t ? t.month || keyOfISO(t.date) : curKey(),
      type: t ? t.type || 'expense' : 'expense',
      cat: t ? t.cat : 'other',
      source: t ? getTxSrc(t) : 'cash',
      rec: !!ex,
      recDay: ex ? ex.day_of_month : t && t.date ? parseInt(String(t.date).slice(8, 10), 10) : 1,
      recFreq: ex ? recEvery(ex) : 1,
      recSrc: ex ? ex.source || 'cash' : t ? getTxSrc(t) : 'cash',
    };
  });
  if (!t) return <Sheet title="עריכת פעולה"><div className="empty-s">הפעולה נמחקה</div></Sheet>;
  const origMonth = t.month || keyOfISO(t.date);
  const set = (k) => (e) => setF({ ...f, [k]: e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e });

  // בעו"ש, שינוי תאריך מציע את התקופה של התאריך החדש (אם הפעולה הייתה בתקופה של התאריך)
  function onDate(e) {
    const d = e.target.value;
    const patch = { date: d };
    if (d && f.source !== 'credit' && origMonth === keyOfISO(t.date)) patch.month = keyOfISO(d);
    setF({ ...f, ...patch });
  }
  const byDate = f.date ? keyOfISO(f.date) : '';
  const perNote = f.month && byDate && f.month !== byDate
    ? 'התאריך שייך לתקופה ' + periodLabel(byDate) + ', אבל הפעולה תיספר בתקופה שבחרת: בהוצאות, בהכנסות וביתרת הסגירה שלה.' : '';

  async function save() {
    const amt = parseFloat(f.amount), desc = noTags(f.description.trim());
    if (!desc || !amt || !f.date) return toast('נא למלא כל השדות');
    if (!(amt > 0)) return toast('הסכום חייב להיות חיובי. לזיכוי או החזר בחר "הכנסה"');
    const before = { ...t };
    const body = { description: desc, amount: amt, type: f.type, cat: f.cat, date: f.date, month: f.month, source: f.source };
    try {
      await updateTx(t.id, body);
      await syncRec(before, { ...t, ...body });
      closeModal();
      toast('✅ נשמר');
    } catch (e) {
      toast('שגיאה: ' + e.message);
    }
  }

  async function del() {
    if (!(await confirmDialog({ title: 'מחיקת פעולה', body: '"' + t.description + '" ' + fmt(t.amount) + '. אי אפשר לבטל.', okLabel: 'מחק', danger: true }))) return;
    try { await deleteTx(t.id); closeModal(); toast('🗑️ נמחק'); } catch (e) { toast('שגיאה: ' + e.message); }
  }

  /* סנכרון החיוב הקבוע: התבנית נמצאת לפי העסקה כפי שהייתה לפני העריכה, אחרת שינוי סכום יוצר תבנית שנייה */
  async function syncRec(before, after) {
    const ex = findRecFor(before) || findRecFor(after);
    let day = parseInt(f.recDay, 10) || 1;
    day = Math.min(31, Math.max(1, day));
    if (f.rec) {
      const recSrc = after.type === 'income' ? 'cash' : f.recSrc;
      const fq = parseInt(f.recFreq, 10) || 1;
      const body = { description: after.description, amount: Math.abs(Number(after.amount)), type: after.type || 'expense', cat: after.cat, day_of_month: day, source: recSrc };
      const sameFq = (Number(ex && ex.every_months) || 1) === fq;
      if (fq > 1 || (ex && !sameFq)) {
        body.every_months = fq;
        body.start_month = ex && sameFq && ex.start_month ? ex.start_month : parseInt(String(after.date).slice(5, 7), 10);
      }
      if (ex) {
        const same = ex.day_of_month === day && ex.cat === after.cat && (ex.source || 'cash') === recSrc && sameFq && ex.description === after.description &&
          Math.abs(Number(ex.amount) - body.amount) < 0.5 && (ex.type || 'expense') === body.type;
        if (same) return;
        await recWrite(ex.id, body);
        toast('🔁 התבנית החוזרת עודכנה');
      } else {
        await recWrite(null, body);
        toast('🔁 נוספה כחיוב קבוע');
      }
    } else if (ex) {
      await deleteRec(ex.id);
      toast('הוסרה מהפעולות החוזרות');
    }
  }

  return (
    <Sheet title="עריכת פעולה">
      <div className="mb12"><Seg kind="type" value={f.type} onChange={(v) => setF({ ...f, type: v, cat: cats(v).some((c) => c.id === f.cat) ? f.cat : cats(v)[0].id })} options={[['expense', 'הוצאה', 'exp'], ['income', 'הכנסה', 'inc']]} /></div>
      <div className="mb12"><label className="lbl">תיאור</label><input value={f.description} onChange={set('description')} /></div>
      <div className="form-row">
        <div><label className="lbl">סכום (₪)</label><input type="number" inputMode="decimal" value={f.amount} onChange={set('amount')} /></div>
        <div><label className="lbl">תאריך</label><input type="date" value={f.date} onChange={onDate} /></div>
      </div>
      <div className="form-row">
        <div><label className="lbl">קטגוריה</label>
          <select value={f.cat} onChange={set('cat')}>{cats(f.type).map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}</select></div>
        <div><label className="lbl">שייך לתקופה</label>
          <select value={f.month} onChange={set('month')}>
            {periodOptions([origMonth, f.month]).map((k) => <option key={k} value={k}>{periodShort(k)}{k === curKey() ? ' (נוכחית)' : ''}</option>)}
          </select></div>
      </div>
      {perNote && <div className="note mb12">{perNote}</div>}
      <div className="mb12"><label className="lbl">איך שולם</label>
        <Seg value={f.source} onChange={(v) => setF({ ...f, source: v })} options={[['cash', '🏦 עו"ש / מזומן'], ['credit', '💳 כרטיס אשראי']]} />
      </div>
      <div className="box2 mb12">
        <label className="check" style={{ margin: 0 }}>
          <input type="checkbox" checked={f.rec} onChange={set('rec')} />
          <span>{f.type === 'income' ? 'הכנסה קבועה (חוזרת)' : 'חיוב קבוע (חוזר)'}<small>נכנס לתחזית התזרים גם בחודשים הבאים</small></span>
        </label>
        {f.rec && (
          <div className="mt10">
            <div className="form-row">
              <div><label className="lbl">יום בחודש</label><input type="number" min="1" max="31" value={f.recDay} onChange={set('recDay')} /></div>
              <div><label className="lbl">תדירות</label><select value={f.recFreq} onChange={set('recFreq')}>{FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            </div>
            {f.type !== 'income' && <>
              <label className="lbl">מאיפה יורד</label>
              <Seg value={f.recSrc} onChange={(v) => setF({ ...f, recSrc: v })} options={[['cash', '🏦 עו"ש'], ['credit', '💳 אשראי']]} />
              <div className="note mt6">{f.recSrc === 'credit' ? 'מחויב בכרטיס, ולכן לא נספר בנפרד בתחזית. הוא כבר כלול בהערכת החיוב המרוכז.' : 'יורד ישירות מהעו"ש, ולכן נכנס לתחזית כחיוב נפרד בתאריך שלו.'}</div>
            </>}
          </div>
        )}
      </div>
      <div className="row">
        <button className="ubtn ubtn-primary" style={{ flex: 1 }} onClick={save}>שמור שינויים</button>
        <button className="ubtn ubtn-ghost danger-text" onClick={del}><Icon name="trash" size={16} />מחק</button>
      </div>
    </Sheet>
  );
}
